import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import { merge } from 'lodash';
import Amplify, { Auth } from 'aws-amplify';
import { ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { withClientState } from 'apollo-link-state';
import AWSAppSyncClient, { AUTH_TYPE, createAppSyncLink, createLinkWithCache } from 'aws-appsync';
import { InMemoryCache, IntrospectionFragmentMatcher, defaultDataIdFromObject } from 'apollo-cache-inmemory';
import introspectionQueryResultData from './graphql/fragmentTypes';
import currentUser from './graphql/resolvers/currentUser';
import fieldState from './graphql/resolvers/fieldState';
import subscriptionResolver from './graphql/resolvers/subscriptionEvents';

let AppSyncConfig;

if (process.env.REACT_APP_BACKEND_ENV) {
  // eslint-disable-next-line
  AppSyncConfig = require(`./aws-exports/aws-exports.${process.env.REACT_APP_BACKEND_ENV}`).default;
} else {
  // eslint-disable-next-line
  AppSyncConfig = require('./aws-exports').default;
}

// window.LOG_LEVEL = 'DEBUG';

const memCache = new InMemoryCache({
  //   As per Apollo Client Documentation
  //   Pass the necessary schema knowledge to the Apollo Client cache
  //   so unions and interfaces can be accurately matched and results
  //   validated before writing them into the store.
  //   https://www.apollographql.com/docs/react/advanced/fragments#fragment-matcher
  fragmentMatcher: new IntrospectionFragmentMatcher({
    introspectionQueryResultData
  }),
  dataIdFromObject: object => {
    /* eslint-disable no-underscore-dangle */
    switch (object.__typename) {
      case 'CurrentUser':
        return object.__typename;
      case 'AssessmentEvent':
        return `${object.__typename}${object.fieldId}${object.eventType}`;
      case 'User':
        return `User:${object.email}`; // use `email` as the primary key
      default:
        return defaultDataIdFromObject(object); // fall back to default handling
    }
    /* eslint-enable no-underscore-dangle */
  }
});

// https://www.apollographql.com/docs/link/links/state#organize
const stateLink = createLinkWithCache(cache =>
  withClientState({
    ...merge(currentUser, subscriptionResolver, fieldState),
    cache
  })
);

Amplify.configure(AppSyncConfig);

const appSyncLink = createAppSyncLink({
  disableOffline: true,
  url: AppSyncConfig.aws_appsync_graphqlEndpoint,
  region: AppSyncConfig.aws_project_region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => (await Auth.currentSession()).getIdToken().getJwtToken()
  },
  // https://stackoverflow.com/questions/48495338/how-to-upload-file-to-aws-s3-using-aws-appsync
  complexObjectsCredentials: () => Auth.currentCredentials()
});

// global apollo error hook
const errorLink = onError(({ graphQLErrors, networkError }) => {
  let errorMsg = 'No error message :(';
  if (networkError) {
    if (networkError.errorMessage) {
      errorMsg = networkError.errorMessage;
    } else {
      errorMsg = networkError;
    }
  } else if (graphQLErrors) {
    const errorArr = graphQLErrors.map(error => error.message);
    errorMsg = errorArr.join(', ');
  }
  console.error('** Apollo graphql errors: %s', errorMsg);
});

const link = ApolloLink.from([errorLink, stateLink, appSyncLink]);

export const aWSAppSyncClient = new AWSAppSyncClient({ disableOffline: true }, { link, cache: memCache });

export default aWSAppSyncClient;
